import type { PermissionEnum } from '@Application/Security/Roles';
import FetchUserPermissions from '@Application/Security/Roles/Queries/FetchUserPermissions';
import fetchUserPermissions from '@Application/Security/Roles/fetchUserPermissions';
import useAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useAuthenticatedUser';
import PermissionContext, {
	type PermissionContextValue,
} from '@UI/Providers/PermissionProvider/PermissionContext';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

type Props = {
	readonly children: React.ReactNode;
};

/**
 * Mounted below `AuthenticatedUserProvider`. Gating is entirely inside `query`: it never dispatches
 * while the session is not `'authenticated'`, which is what keeps an anonymous visitor from ever
 * calling the permissions endpoint on the public pages.
 */
const PermissionProvider: React.FC<Props> = ({ children }) => {
	const dispatch = useDispatch();
	const { user, sessionStatus } = useAuthenticatedUser();

	const { data: permissionsList } = useStoreQuery({
		selector: (state) => state.userPermissionsStore.permissions,
		stopOn: (value) => value !== null,
		query: () => {
			if (sessionStatus !== 'authenticated' || user === null) {
				return Promise.resolve();
			}

			return dispatch(fetchUserPermissions(new FetchUserPermissions(user.id)));
		},
		deps: [user?.id ?? null],
	});

	const hasPermission = React.useCallback(
		(permission: PermissionEnum): boolean => permissionsList?.has(permission) ?? false,
		[permissionsList],
	);

	const hasAllPermissions = React.useCallback(
		(permissions: readonly PermissionEnum[]): boolean =>
			permissions.every((permission) => hasPermission(permission)),
		[hasPermission],
	);

	const hasAnyPermission = React.useCallback(
		(permissions: readonly PermissionEnum[]): boolean =>
			permissions.some((permission) => hasPermission(permission)),
		[hasPermission],
	);

	const permissions = React.useMemo<readonly string[]>(
		() => permissionsList?.list.map((entry) => entry.value) ?? [],
		[permissionsList],
	);

	const isResolved = permissionsList !== null;

	const value = React.useMemo<PermissionContextValue>(
		() => ({ permissions, hasPermission, hasAllPermissions, hasAnyPermission, isResolved }),
		[permissions, hasPermission, hasAllPermissions, hasAnyPermission, isResolved],
	);

	return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export default PermissionProvider;
